'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@upzi/web-shell/ui/button';
import { Card } from '@upzi/web-shell/ui/card';
import { cn } from '@/lib/utils';
import { getCompanyProfilePath } from '@/services/company-profile/routes';
import type { JobDetail } from '@/services/jobs/model';
import { CompanyLogo } from './company-logo';
import { CompanyActivityLink } from './company-activity-link';

const PAGE_COLLAPSED_DESCRIPTION_LINES = 11;

const HTML_MARKUP_PATTERN = /<\/?[a-z][\s\S]*>/i;
const UNSAFE_HTML_TAG_PATTERN =
  /<\/?(script|style|iframe|object|embed|link|meta|base|form|input|button|textarea|select|option)[^>]*>/gi;
const UNSAFE_HTML_ATTRIBUTE_PATTERN =
  /\s(?:on\w+|style|srcdoc)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi;
const UNSAFE_URL_ATTRIBUTE_PATTERN =
  /\s(?:href|src)\s*=\s*(["'])\s*(?:javascript|data):[^"']*\1/gi;

type JobCompanyCardProps = {
  jobDetail: Pick<JobDetail, 'company'>;
  variant: 'page' | 'preview';
};

function sanitizeCompanyDescriptionHtml(description: string) {
  return description
    .replace(UNSAFE_HTML_TAG_PATTERN, '')
    .replace(UNSAFE_HTML_ATTRIBUTE_PATTERN, '')
    .replace(UNSAFE_URL_ATTRIBUTE_PATTERN, '');
}

function normalizeCompanyDescription(description: string) {
  return description
    .replace(/\r\n?/g, '\n')
    .replace(/\\r\\n|\\n|\\r/g, '\n')
    .trim();
}

export function JobCompanyCard({ jobDetail, variant }: JobCompanyCardProps) {
  const [expandedDescription, setExpandedDescription] = useState<string | null>(
    null
  );
  const [canExpand, setCanExpand] = useState(false);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const { company } = jobDetail;
  const companyName = company.name?.trim() || '';
  const companyHref = getCompanyProfilePath(company);
  const description = company.description
    ? normalizeCompanyDescription(company.description)
    : undefined;
  const hasHtmlDescription = Boolean(
    description && HTML_MARKUP_PATTERN.test(description)
  );
  const safeHtmlDescription = description
    ? sanitizeCompanyDescriptionHtml(description)
    : '';
  const companyMeta = company.industryName;
  const isPreview = variant === 'preview';
  const isExpanded = Boolean(
    description && expandedDescription === description
  );

  useEffect(() => {
    const descriptionElement = descriptionRef.current;

    if (isPreview || !descriptionElement || !description) {
      setCanExpand(false);

      return;
    }

    const measureOverflow = () => {
      const computedStyle = window.getComputedStyle(descriptionElement);
      const fontSize = Number.parseFloat(computedStyle.fontSize);
      const parsedLineHeight = Number.parseFloat(computedStyle.lineHeight);
      const lineHeight = Number.isNaN(parsedLineHeight)
        ? fontSize * 1.2
        : parsedLineHeight;
      const maxCollapsedHeight = lineHeight * PAGE_COLLAPSED_DESCRIPTION_LINES;
      const nextCanExpand =
        descriptionElement.scrollHeight - maxCollapsedHeight > 1;

      setCanExpand(currentCanExpand =>
        currentCanExpand === nextCanExpand ? currentCanExpand : nextCanExpand
      );
    };

    measureOverflow();

    const resizeObserver = new ResizeObserver(measureOverflow);

    resizeObserver.observe(descriptionElement);

    return () => resizeObserver.disconnect();
  }, [description, isPreview]);

  return (
    <Card className="border-alias-outline bg-surface-pure relative w-full min-w-0 overflow-hidden rounded-[18px] border p-5 shadow-none">
      <div
        className={cn(
          'relative flex min-w-0 items-start gap-4',
          isPreview && 'items-center'
        )}
      >
        <Link
          href={companyHref}
          target="_blank"
          rel="noopener"
          aria-label={`Mở trang công ty ${companyName || 'Nhà tuyển dụng'} trong tab mới`}
          className="focus-visible:ring-primary/50 shrink-0 rounded-[12px] focus-visible:ring-2 focus-visible:outline-none"
        >
          <CompanyLogo
            job={jobDetail}
            size={48}
            className="rounded-[12px]"
            imageClassName="p-0"
          />
        </Link>
        <div className={cn('min-w-0 flex-1', !isPreview && 'pr-6')}>
          <h2 className="font-display text-on-surface text-lg leading-7 font-semibold [overflow-wrap:anywhere] whitespace-normal">
            <Link
              href={companyHref}
              target="_blank"
              rel="noopener"
              className="hover:text-on-surface underline-offset-2 hover:underline focus-visible:outline-none"
            >
              {companyName}
            </Link>
          </h2>
          {companyMeta ? (
            <p className="font-body text-on-surface text-ui-base leading-ui-base mt-1 font-normal">
              {companyMeta}
            </p>
          ) : null}
        </div>
        {isPreview ? (
          <CompanyActivityLink companyHref={companyHref} className="shrink-0" />
        ) : null}
      </div>

      {description ? (
        <>
          <div
            ref={descriptionRef}
            className={cn(
              'font-body text-on-surface text-ui-base leading-ui-base relative mt-2 font-normal whitespace-pre-line',
              '[&_a]:text-primary [&_a]:underline [&_li]:ml-5 [&_li]:list-disc [&_ol:not(:first-child)]:mt-2 [&_ol>li]:list-decimal [&_p:not(:first-child)]:mt-2 [&_ul:not(:first-child)]:mt-2',
              !isPreview && !isExpanded && 'line-clamp-[11]'
            )}
            {...(hasHtmlDescription
              ? {
                  dangerouslySetInnerHTML: { __html: safeHtmlDescription },
                }
              : {})}
          >
            {hasHtmlDescription ? null : description}
          </div>
          {!isPreview ? (
            <div className="h-12">
              {canExpand ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    setExpandedDescription(value =>
                      value === description ? null : description
                    )
                  }
                  className="font-body text-base-foreground hover:text-base-foreground !text-ui-base leading-ui-base relative mt-2 h-10 gap-1.5 rounded-xl px-4 py-2 font-medium hover:bg-transparent"
                >
                  {isExpanded ? 'Thu gọn' : 'Xem thêm'}
                  {isExpanded ? (
                    <ChevronUp className="size-4" />
                  ) : (
                    <ChevronDown className="size-4" />
                  )}
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}

      {!isPreview ? (
        <CompanyActivityLink
          companyHref={companyHref}
          className="mt-4 w-full"
        />
      ) : null}
    </Card>
  );
}
